import kfetch from '@kareo/kfetch';
import getSessionId from '../utils/sessionId';

export const Roles = {
  Provider: 'Provider',
  SystemAdministrator: 'System Administrator',
  ClinicalAssistant: 'Clinical Assistant',
  OfficeStaff: 'Office Staff',
  Biller: 'Biller',
  FinancialAnalyst: 'Financial Analyst',
};

export const PrivilegeTypes = {
  Labs_View: 14,
  Calendar_Setup: 22,
  Patient_Appointments: 31,
  Messages: 32,
  Patient_Chart: 34,
  Notes: 35,
};

export const AccessLevels = {
  Read: 1,
  Write: 2,
};

class AuthenticatedUser {
  constructor() {
    this.instance = null;
    this.privileges = {};
    this.roles = {};
  }

  async getAuthenticatedUser(config = {}) {
    if (!this.authenticatedUserPromise) {
      const sessionId = getSessionId();

      this.authenticatedUserPromise = kfetch(
        `/dashboard-calendar-ui/api/User/UserWithRoles?sessionId=${encodeURIComponent(
          sessionId,
        )}`,
        config,
      ).then(response => {
        // Create a lookup tabled based on role name
        let roles = {};
        response.roles.forEach(r => {
          roles[`${r.name}`] = r.id;
        });
        this.roles = roles;

        return response;
      });
    }
    return this.authenticatedUserPromise;
  }

  async getPrivileges(config = {}) {
    if (!this.privilegesPromise) {
      const sessionId = getSessionId();

      this.privilegesPromise = kfetch(
        `/dashboard-calendar-ui/api/Privileges?sessionId=${encodeURIComponent(
          sessionId,
        )}`,
        config,
      ).then(response => {
        // Create a lookup tabled based on the ID
        let privileges = {};
        if (Array.isArray(response)) {
          response.forEach(p => (privileges[`${p.id}`] = p.accessType));
        }
        this.privileges = privileges;

        return response;
      });
    }
    return this.privilegesPromise;
  }

  async getRolesLookup() {
    if (Object.keys(this.roles).length === 0) {
      return this.getAuthenticatedUser().then(response => {
        return this.roles;
      });
    }
    return this.roles;
  }

  async getPrivilegesLookup() {
    if (Object.keys(this.privileges).length === 0) {
      return this.getPrivileges().then(response => {
        return this.privileges;
      });
    }
    return this.privileges;
  }

  async hasRole(role) {
    if (Object.keys(this.roles).length === 0) {
      await this.getAuthenticatedUser();
    }

    return this.roles.hasOwnProperty(role);
  }

  async hasPrivilege(privilegeType, accessLevel) {
    if (Object.keys(this.privileges).length === 0) {
      await this.getPrivileges();
    }

    // Assume read access if the user has write access.
    return this.privileges[privilegeType] >= accessLevel;
  }

  static get() {
    if (!this.instance) {
      this.instance = new AuthenticatedUser();
    }

    return this.instance;
  }

  /**
   * This should be used inside of tests in order to reset roles and privilege sets
   */
  clearCache() {
    this.roles = {};
    this.privileges = {};
    this.authenticatedUserPromise = null;
    this.privilegesPromise = null;
  }
}

// Export a single instance of the User class
export const authenticatedUser = AuthenticatedUser.get();
